import { FC, useState, useEffect } from "react";
import {
  Box,
  List,
  Paper,
  Typography,
  Divider,
  CircularProgress,
  Button,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InternalPage from "../internal/InternalPage";
import { InternalEmailThreadComponent } from "../internal/Email/InternalEmailThreadComponent";
import { apiClient } from "../utils/apiClient";
import { useSearchParams } from "react-router-dom";
import { EmailListItem } from "../internal/Email/EmailListItemComponent";
import {
  EmailMessage,
  EmailSearchMessagesResponse,
  EmailSearchMessagesBody,
} from "@services/documents/email/types";

const cleanTags = (tags: string[]) => {
  return tags
    .map((tag) => tag.trim().toLowerCase())
    .filter((x) => x && x !== "");
};

export const MessagesWithExtractedDataFromJobPage: FC = () => {
  const [messages, setMessages] = useState<EmailMessage[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [selectedMessage, setSelectedMessage] =
    useState<EmailMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("query") || "from:me"
  );
  const [tags, setTags] = useState<string[]>(
    searchParams.get("tags")?.split(",") || []
  );
  const [searching, setSearching] = useState(false);
  const [onlyWithObjects, setOnlyWithObjects] = useState(false);

  const handleMessageSelect = (message: EmailMessage) => {
    setSelectedMessage(message);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("messageId", message.messageId);
    setSearchParams(newParams);
  };

  const fetchMessages = async (query: string, tags: string[]) => {
    try {
      setError(null);
      setSearching(true);
      const response =
        await apiClient.post<EmailSearchMessagesResponse>(
          "/documents/email/get_messages_analyzed_in_job",
          {
            query,
            tags: cleanTags(tags),
            limit: 500,
            offset: 0,
          } satisfies EmailSearchMessagesBody
        );
      setMessages(response.messages);
      setNextPageToken(response.total > response.messages.length ? 'has_more' : undefined);
    } catch (err) {
      setError("Failed to load messages");
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(searchQuery, tags);
  }, []);

  const handleSearch = () => {
    setMessages([]);
    setNextPageToken(undefined);
    setSearchParams({ query: searchQuery, tags: tags.join(",") });
    fetchMessages(searchQuery, tags);
  };

  if (error) {
    return <InternalPage>{error}</InternalPage>;
  }

  return (
    <InternalPage
      containerStyle={{
        height: "calc(100vh - 100px)",
        pt: "20px",
        marginTop: 0,
        boxSizing: "content-box",
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {/* Search Bar */}
        <Paper
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Job"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            placeholder="Enter job name or job id..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Tags"
            placeholder="Comma-separated tags..."
            value={tags.join(",")}
            onChange={(e) =>
              setTags(
                e.target.value.split(",").map((tag) => tag.trim().toLowerCase())
              )
            }
          />
          <IconButton onClick={handleSearch} disabled={searching}>
            {searching ? <CircularProgress size={24} /> : <SearchIcon />}
          </IconButton>
        </Paper>

        <Box display="flex" flex={1} overflow="hidden">
          {/* Left Sidebar - Messages List */}
          <Paper
            sx={{
              width: 420,
              height: "100%",
              overflowY: "auto",
              borderRight: 1,
              borderColor: "divider",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Typography variant="h6" fontWeight="bold">
                Messages
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyWithObjects}
                    onChange={() => setOnlyWithObjects(!onlyWithObjects)}
                    size="small"
                  />
                }
                label="Only With Objects"
              />
            </Box>
            <List>
              {messages
                .filter((message) =>
                  onlyWithObjects
                    ? message.metadata?.extracted_objects &&
                      message.metadata.extracted_objects.length > 0
                    : true
                )
                .map((message) => (
                  <Box key={message.messageId}>
                    <EmailListItem
                      id={message.messageId}
                      sender={message.fromEmail}
                      threadId={message.threadId}
                      subject={message.subject}
                      date={message.date.toString()}
                      snippet={message.snippet}
                      tags={
                        message.metadata?.extracted_objects &&
                        message.metadata.extracted_objects.length > 0
                          ? ["contains objects"]
                          : []
                      }
                      isSelected={
                        selectedMessage?.messageId === message.messageId
                      }
                      onSelect={() => handleMessageSelect(message)}
                    />
                    <Divider />
                  </Box>
                ))}
            </List>
          </Paper>

          {/* Right Column - Selected Email Thread */}
          <Box flex={1} height="100%" p={2}>
            {selectedMessage && selectedMessage.threadId ? (
              <InternalEmailThreadComponent
                threadId={selectedMessage.threadId}
                selectedMessageId={selectedMessage.messageId}
                showMessageMetadata={true}
                additionalMetadataObject={
                  selectedMessage.metadata?.extracted_objects
                    ? selectedMessage.metadata.extracted_objects.map(JSON.parse)
                    : null
                }
              />
            ) : (
              <Typography color="text.secondary">
                Select a message to view its email thread
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </InternalPage>
  );
};
